import { CellContext, ColumnDef, Row } from "@tanstack/react-table";
import { EditIcon, EyeIcon, EyeOffIcon, TrashAltIcon } from "assets/icons";
import { abbreviateNumber } from "helpers";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { SharingSocials } from "modules/sharing";
import { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ItemsTable, TableItemActionsControl } from "ui";

import { useChangeProductVariantVisibility } from "../../apis";
import { EProductVariantVisibility } from "../../constants";
import { ProductsListItem } from "../../product.types";
import { ProductCell } from "../ProductCell";
import { PriceCell } from "./PriceCell";
import { ProductsTableProps } from "./ProductsTable.types";

import styles from "./ProductsTable.module.scss";

const MemoProductCell = memo(({ row }: { row: Row<ProductsListItem> }) => (
  <ProductCell
    name={row.original.name}
    image={row.original.image}
    subtitle={row.original.categoryName}
    visibility={row.original.visibility}
  />
));

const VISIBILITY_LABELS: Record<EProductVariantVisibility, string> = {
  [EProductVariantVisibility.Visible]: "Make Visible",
  [EProductVariantVisibility.PackOnly]: "Make Pack Only",
  [EProductVariantVisibility.Hidden]: "Make Hidden"
};

MemoProductCell.displayName = "MemoProductCell";

const ProductsTable = ({
  products,
  sorting,
  isLoading,
  setSorting,
  onDeleteProduct
}: ProductsTableProps) => {
  const { currentLanguage } = useLanguage();
  const { user } = useProfile({ enabled: false });
  const refetchProducts = useRefetchQuery(["products"]);
  const navigate = useNavigate();
  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const { canDelete, canUpdate, canChangeVisibility } = useMemo(() => {
    return {
      canDelete:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.DeleteProductVariants
        ),
      canChangeVisibility:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.ChangeProductVariantVisibilityForResellers
        ),
      canUpdate:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.UpdateProducts)
    };
  }, [user]);

  const changeProductVariantVisibility = useChangeProductVariantVisibility({
    onSuccess: refetchProducts
  });

  const columns: Array<ColumnDef<ProductsListItem, ProductsListItem[]>> =
    useMemo(
      () => [
        {
          id: "name",
          accessorKey: "name",
          header: t("Product"),
          size: 42,
          minSize: 42,
          cell: MemoProductCell
        },
        {
          id: "discountedPrice",
          accessorKey: "discountedPrice",
          header: t("Standard Price"),
          size: 17,
          minSize: 17,
          cell: ({ row }) => (
            <PriceCell
              price={row.original.price}
              discountedPrice={row.original.discountedPrice}
            />
          )
        },
        {
          header: "WA",
          enableSorting: false,
          size: 5,
          minSize: 5,
          cell: ({ row }) =>
            abbreviateNumber(
              row.original.sharingsCount?.socialMedia.find(
                (media) => media.name === SharingSocials.WhatsApp
              )?.count ?? 0,
              1
            )
        },
        {
          header: "FB",
          enableSorting: false,
          size: 5,
          minSize: 5,
          cell: ({ row }) =>
            abbreviateNumber(
              row.original.sharingsCount?.socialMedia.find(
                (media) => media.name === SharingSocials.Facebook
              )?.count ?? 0,
              1
            )
        },
        {
          header: "IN",
          enableSorting: false,
          size: 5,
          minSize: 5,
          cell: ({ row }) =>
            abbreviateNumber(
              row.original.sharingsCount?.socialMedia.find(
                (media) => media.name === SharingSocials.Instagram
              )?.count ?? 0,
              1
            )
        },
        {
          header: "TT",
          enableSorting: false,
          size: 5,
          minSize: 5,
          cell: ({ row }) =>
            abbreviateNumber(
              row.original.sharingsCount?.socialMedia.find(
                (media) => media.name === SharingSocials.TikTok
              )?.count ?? 0,
              1
            )
        },
        {
          header: "SC",
          enableSorting: false,
          size: 5,
          minSize: 5,
          cell: ({ row }) =>
            abbreviateNumber(
              row.original.sharingsCount?.socialMedia.find(
                (media) => media.name === SharingSocials.Snapchat
              )?.count ?? 0,
              1
            )
        },
        {
          header: "Other",
          enableSorting: false,
          size: 5,
          minSize: 5,
          cell: ({ row }) =>
            abbreviateNumber(
              row.original.sharingsCount?.socialMedia.find(
                (media) => media.name === null
              )?.count ?? 0,
              1
            )
        },
        {
          accessorKey: "productsSold",
          header: t("Products sold"),
          size: 13,
          minSize: 13,
          cell: ({ row }) =>
            abbreviateNumber(Number(row.original.productsSold), 1)
        },
        ...(canChangeVisibility || canDelete || canUpdate
          ? [
              {
                accessorKey: "dotsColumn",
                header: () => null,
                size: 3,
                minSize: 3,
                cell: ({
                  row
                }: CellContext<ProductsListItem, ProductsListItem[]>) => (
                  <TableItemActionsControl
                    actionsList={[
                      ...(canUpdate
                        ? [
                            {
                              label: t("Edit"),
                              Icon: (
                                <EditIcon
                                  fill={"#090914"}
                                  width={18}
                                  height={18}
                                />
                              ),
                              onClick: () =>
                                navigate(
                                  `/products/edit/${row.original.categoryId}/${
                                    row.original.productId
                                  }`
                                )
                            }
                          ]
                        : []),
                      ...(canChangeVisibility
                        ? Object.values(EProductVariantVisibility)
                            .filter(
                              (visibility) =>
                                visibility !== row.original.visibility
                            )
                            .map((visibility) => ({
                              label: t(VISIBILITY_LABELS[visibility]),
                              Icon:
                                visibility ===
                                EProductVariantVisibility.Visible ? (
                                  <EyeIcon
                                    fill={"#090914"}
                                    width={18}
                                    height={18}
                                  />
                                ) : (
                                  <EyeOffIcon
                                    fill={"#090914"}
                                    width={18}
                                    height={18}
                                  />
                                ),
                              onClick: () => {
                                changeProductVariantVisibility.mutate({
                                  id: row.original.id,
                                  visibility
                                });
                              }
                            }))
                        : []),
                      ...(canDelete
                        ? [
                            {
                              label: t("Delete"),
                              Icon: (
                                <TrashAltIcon
                                  fill={"#ff0033"}
                                  width={18}
                                  height={18}
                                />
                              ),
                              labelColor: "#ff0033",
                              onClick: () => onDeleteProduct(row.original.id)
                            }
                          ]
                        : [])
                    ]}
                    rowIndex={row.index}
                    setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
                    openedItemOptionsIndex={openedItemOptionsIndex}
                  />
                ),
                enableSorting: false
              }
            ]
          : [])
      ],
      [products, user, openedItemOptionsIndex, currentLanguage]
    );

  return (
    <ItemsTable
      className={styles.productTable}
      columns={columns}
      data={products}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={75}
      getLinkUrl={({ categoryId, productId, id }) => {
        return `/products/details/${categoryId}/${productId}/${id}/boxes`;
      }}
      linkState={{ productsListSearch: window.location.search }}
    />
  );
};

export default ProductsTable;
